/**
 * Typed auth-API client.
 *
 * Mirrors the FastAPI Pydantic schemas in
 *   hooten-young-sales/src/hy_sales/schemas/auth.py
 *
 * All auth endpoints live on the `sales` domain (the auth schema is
 * owned by hooten-young-sales — the marketing backend only validates
 * the JWT).
 */

import { apiGet, apiPatch, apiPost } from './client';

// =====================================================================
// Types — keep in lockstep with hy_sales/schemas/auth.py
// =====================================================================

export interface RolePublic {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  is_system: boolean;
}

export interface UserDetail {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: 'pending' | 'active' | 'rejected' | 'disabled';
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
  roles: RolePublic[];
}

export interface TokenResponse {
  access_token: string;
  token_type: 'bearer';
  expires_in: number;
  must_change_password: boolean;
}

export interface SignupResponse {
  user_id: string;
  status: string;
  message: string;
}

export interface MessageResponse {
  message: string;
}

export interface SignupRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface UpdateMeRequest {
  first_name: string;
  last_name: string;
}

// =====================================================================
// Endpoint wrappers
// =====================================================================

export function signupRequest(payload: SignupRequest): Promise<SignupResponse> {
  return apiPost<SignupResponse>('sales', '/api/auth/signup', payload);
}

export function loginRequest(payload: LoginRequest): Promise<TokenResponse> {
  return apiPost<TokenResponse>('sales', '/api/auth/login', payload);
}

/**
 * Swap a still-valid token for a fresh one.
 *
 * Access tokens are deliberately short-lived; this is what lets an
 * active session outlive one, so the short lifetime costs security
 * exposure rather than the user's afternoon.
 */
export function renewRequest(): Promise<TokenResponse> {
  return apiPost<TokenResponse>('sales', '/api/auth/renew', {});
}

export function fetchMe(): Promise<UserDetail> {
  return apiGet<UserDetail>('sales', '/api/auth/me');
}

export function updateMeRequest(payload: UpdateMeRequest): Promise<UserDetail> {
  return apiPatch<UserDetail>('sales', '/api/auth/me', payload);
}

export function forgotPasswordRequest(payload: ForgotPasswordRequest): Promise<MessageResponse> {
  return apiPost<MessageResponse>('sales', '/api/auth/forgot-password', payload);
}

export function resetPasswordRequest(payload: ResetPasswordRequest): Promise<TokenResponse> {
  return apiPost<TokenResponse>('sales', '/api/auth/reset-password', payload);
}

export function changePasswordRequest(payload: ChangePasswordRequest): Promise<TokenResponse> {
  return apiPost<TokenResponse>('sales', '/api/auth/change-password', payload);
}
